/**
 * What the scaled-space pass draws for a system (M3 plan R8), and its mapping from the in-system layout. The system
 * frame is float64 metres with the star at the origin (plan §2.2), so the star's position is [0, 0, 0].
 */
import type { SystemLayout, Vec3T } from '../../galaxy/layout/types'
import type { BodyKind } from '../../galaxy/types'

export interface ScaledStarSpec {
  position: Vec3T
  radiusM: number
  color: string
  luminosity: number
  seed: number
}

export interface ScaledBodySpec {
  id: string
  kind: BodyKind
  position: Vec3T
  radiusM: number
  rings: boolean
  rotationPeriodS: number
  tiltRad: number
  seed: number
}

export interface ScaledSystemSpec {
  star: ScaledStarSpec
  bodies: readonly ScaledBodySpec[]
}

const ORIGIN: Vec3T = [0, 0, 0]

/** The scaled-space spec of a system layout: the star at the origin and every body as placed. */
export function scaledSpecFromLayout(layout: SystemLayout): ScaledSystemSpec {
  const s = layout.star
  return {
    star: { position: ORIGIN, radiusM: s.radiusM, color: s.color, luminosity: s.luminosity, seed: s.seed },
    bodies: layout.bodies.map((b) => ({
      id: b.id,
      kind: b.kind,
      position: b.position,
      radiusM: b.radiusM,
      rings: b.rings,
      rotationPeriodS: b.rotationPeriodS,
      tiltRad: b.tiltRad,
      seed: b.seed,
    })),
  }
}
